import { OpenInNew } from '@mui/icons-material';
import { EditType, v1PeopleProfileEditsCreate } from '@on3/api';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Link } from '@on3/ui-lib/components/Link';
import { externalApi, useToast } from '@on3/ui-lib/index';
import { filterNumber } from '@on3/ui-lib/utils/filters';
import { yup } from '@on3/ui-lib/utils/yup';
import { useModal } from '@public/components/Modals';
import { usePlayerProfile } from '@public/contexts/PlayerProfile/PlayerProvider';
import { useRouter } from 'next/router';
import { useState } from 'react';

import styles from './NcaaIdModal.module.scss';

const schema = yup.object().shape({
  ncaaId: yup
    .string()
    .test(
      'is-ncaa-id',
      'Invalid NCAA ID. Valid NCAA IDs are 10 digits.',
      (value) => {
        if (!value) return true;

        return /^\d{10}$/.test(value);
      },
    )
    .required(),
});

export const NcaaIdModal = () => {
  const {
    state: { player },
    setState,
  } = usePlayerProfile();
  const router = useRouter();
  const { notify } = useToast();
  const { closeModal } = useModal();

  const [ncaaId, setNcaaId] = useState<string>(player.ncaaId || '');

  const handleSubmit = async (values: { ncaaId: string }) => {
    try {
      await v1PeopleProfileEditsCreate(externalApi, player.key, {
        edit: {
          $type: 'ncaaId',
          editType: player.ncaaId ? EditType.Update : EditType.Add,
          value: values.ncaaId,
        },
      });

      // TODO: Uncomment the following lines when the API is ready
      // setState((prev) => ({
      //   ...prev,
      //   player: {
      //     ...prev.player,
      //     ncaaId: values.ncaaId,
      //   },
      // }));

      notify('NCAA ID update submitted successfully.', {
        type: 'success',
      });

      // TODO: Remove this line when the API is ready
      router.reload();
    } catch (error) {
      notify('Failed to update NCAA ID.', {
        type: 'error',
      });
    } finally {
      closeModal();
    }
  };

  return (
    <div className={styles.modal}>
      <h2>Update NCAA ID</h2>
      <Form
        className={styles.form}
        onSubmit={handleSubmit}
        schema={schema}
        values={{ ncaaId }}
      >
        {({ processing, errors }) => (
          <>
            <Input
              error={errors.ncaaId}
              helperText={
                <Link
                  className={styles.link}
                  href="https://web3.ncaa.org/ecwr3/"
                >
                  Get your NCAA ID <OpenInNew />
                </Link>
              }
              inputMode="numeric"
              label="NCAA ID"
              name="ncaaId"
              onChange={(e) =>
                setNcaaId(filterNumber(e.target.value).slice(0, 10))
              }
              value={ncaaId}
            />
            <div className={styles.actions}>
              <button name="cancel" onClick={closeModal} type="button">
                Cancel
              </button>
              <button
                disabled={processing || !ncaaId}
                name="save"
                type="submit"
              >
                Save
              </button>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};
